import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { extractErrorMessage } from '@/helpers/extractErrorMessage';
import type { AuthResponseDto, LoginCredentials } from '../interfaces';
import type { PermissionRequirement } from '@/interfaces/permission.interface';
import { authService } from '../services/auth.service';
import { satisfiesPermissionRequirement } from '../utils/permission.utils';
import { AUTH_SESSION_CLEARED_EVENT } from '@/constants/authEvents';

export type { LoginCredentials };

export interface AuthState {
	user: AuthResponseDto['user'] | null;
	accessToken: string | null;
	refreshToken: string | null;
	expiresAt: number | null;
	permissions: string[];
	isAuthenticating: boolean;
	error: string | null;
	login: (credentials: LoginCredentials) => Promise<{ success: boolean; message?: string }>;
	logout: () => Promise<void>;
	restoreSession: () => void;
	clearError: () => void;
	hasPermission: (requirement?: PermissionRequirement) => boolean;
}

const restoredSession = authService.hydrateFromStorage();

const getPermissionCodes = (user: AuthResponseDto['user'] | null) =>
	user?.role?.permissions?.map(
		permission => permission.name ?? `${permission.resource}.${permission.action}`,
	) ?? [];

export const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			user: null,
			accessToken: restoredSession?.accessToken ?? null,
			refreshToken: restoredSession?.refreshToken ?? null,
			expiresAt: restoredSession?.expiresAt ?? null,
			permissions: [],
			isAuthenticating: false,
			error: null,
			login: async credentials => {
				set({ isAuthenticating: true, error: null });
				try {
					const response = await authService.login(credentials);
					set({
						user: response.user,
						accessToken: response.accessToken,
						refreshToken: response.refreshToken,
						expiresAt: Date.now() + response.expiresIn * 1000,
						permissions: getPermissionCodes(response.user),
						isAuthenticating: false,
						error: null,
					});
					return { success: true };
				} catch (error) {
					const message = extractErrorMessage(
						error,
						'No pudimos iniciar sesión. Intenta nuevamente.',
					);
					set({
						isAuthenticating: false,
						error: message,
					});
					return { success: false, message };
				}
			},
			logout: async () => {
				const token = get().accessToken;
				set({ isAuthenticating: true });
				try {
					await authService.logout(token);
				} finally {
					set({
						user: null,
						accessToken: null,
						refreshToken: null,
						expiresAt: null,
						permissions: [],
						isAuthenticating: false,
						error: null,
					});
				}
			},
			restoreSession: () => {
				const session = authService.hydrateFromStorage();
				if (session) {
					set({
						accessToken: session.accessToken,
						refreshToken: session.refreshToken,
						expiresAt: session.expiresAt,
					});
				} else {
					set({
						accessToken: null,
						refreshToken: null,
						expiresAt: null,
					});
				}
			},
			clearError: () => set({ error: null }),
			hasPermission: requirement =>
				satisfiesPermissionRequirement(get().permissions, requirement),
		}),
		{
			name: 'cardio-auth-store',
			storage: createJSONStorage(() => sessionStorage),
			partialize: state => ({
				user: state.user,
				accessToken: state.accessToken,
				refreshToken: state.refreshToken,
				expiresAt: state.expiresAt,
				permissions: state.permissions,
			}),
		},
	),
);

if (typeof window !== 'undefined') {
	window.addEventListener(AUTH_SESSION_CLEARED_EVENT, () => {
		useAuthStore.setState({
			user: null,
			accessToken: null,
			refreshToken: null,
			expiresAt: null,
			permissions: [],
			isAuthenticating: false,
			error: null,
		});
	});
}
